import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { normalizePageRequest, PagedResponse, PageRequest } from '@core/models/common.model';
import {
    CreateUserRequest,
    UpdateUserProfileRequest,
    UpdateUserRequest,
    UserResponse,
    UserTransactionHistoryResponse,
} from './user.model';

@Injectable({ providedIn: 'root' })
export class UserApiService {
    private http = inject(HttpClient);

    private readonly apiBaseUrl = environment.apiUrl.endsWith('/')
        ? environment.apiUrl
        : `${environment.apiUrl}/`;

    getUserProfile(): Observable<UserResponse> {
        return this.http.get<UserResponse>(`${this.apiBaseUrl}users/profile`);
    }

    updateUserProfile(user: UpdateUserProfileRequest): Observable<UserResponse> {
        return this.http.put<UserResponse>(`${this.apiBaseUrl}users/profile`, user);
    }

    getUsers(pageRequest?: PageRequest, search?: string | null): Observable<PagedResponse<UserResponse>> {
        const normalizedPageRequest = normalizePageRequest(pageRequest);

        return this.http.get<PagedResponse<UserResponse>>(`${this.apiBaseUrl}user-management/users`, {
            params: {
                search: search ?? '',
                pageNumber: normalizedPageRequest.pageNumber,
                pageSize: normalizedPageRequest.pageSize,
            },
        });
    }

    getUser(userId: string): Observable<UserResponse> {
        return this.http.get<UserResponse>(`${this.apiBaseUrl}user-management/users/${userId}`);
    }

    createUser(user: CreateUserRequest): Observable<UserResponse> {
        return this.http.post<UserResponse>(`${this.apiBaseUrl}user-management/users`, user);
    }

    updateUser(userId: string, user: UpdateUserRequest): Observable<UserResponse> {
        return this.http.put<UserResponse>(`${this.apiBaseUrl}user-management/users/${userId}`, user);
    }

    deactivateUser(userId: string): Observable<UserResponse> {
        return this.http.put<UserResponse>(`${this.apiBaseUrl}user-management/users/${userId}/deactivate`, {});
    }

    activateUser(userId: string): Observable<UserResponse> {
        return this.http.put<UserResponse>(`${this.apiBaseUrl}user-management/users/${userId}/activate`, {});
    }

    getUserHistory(userId: string): Observable<UserTransactionHistoryResponse[]> {
        return this.http.get<UserTransactionHistoryResponse[]>(`${this.apiBaseUrl}user-management/users/${userId}/history`);
    }
}